import { ApiHandler } from '@eshop/api-client'
import type Eshop from '@eshop/type/eshop'
import type Rental from '@eshop/type/rental'

import { mapEshopProducts } from '#/global/service/EshopProductMapper'
import type {
    EshopProductApiResponses,
    EshopProductPart,
    ProductAvailabilityEshopApi,
    ProductDetailEshopApi,
    ProductDetailRentalApi,
    ProductPriceEshopApi,
    ProductSeasonEshopApi,
    ProductStickerEshopListItemApi,
    ProductStickerRentalListItemApi,
    ProductUnitEshopApi,
    RentalProductApiResponses,
    RentalProductPart,
} from '#/global/service/ProductApiTypes'
import { mapRentalProducts } from '#/global/service/RentalProductMapper'

const ESHOP_DETAIL_PATH = '/api/mssql/get-product-detail-eshop-list'
const ESHOP_PRICE_PATH = '/api/mssql/get-product-price-eshop-list'
const ESHOP_UNIT_PATH = '/api/mssql/get-product-unit-eshop-list'
const ESHOP_AVAILABILITY_PATH = '/api/mssql/get-product-availability-eshop-list'
const ESHOP_SEASON_PATH = '/api/mssql/get-product-reward-rule-eshop-list'
const ESHOP_STICKER_PATH = '/api/mssql/get-product-sticker-eshop-list'
const RENTAL_DETAIL_PATH = '/api/mssql/get-product-detail-rental-list'
const RENTAL_STICKER_PATH = '/api/mssql/get-product-sticker-rental-list'

const sendList = async <T>(path: string, parameters: Record<string, string[]>): Promise<T[]> => {
    const api = new ApiHandler({ path, parameters })
    return await api.send<T[]>() ?? []
}

export const ServiceProduct = {
    async getProductEshopByCode<T extends EshopProductPart>(this: void, codes: string | string[], parts: T[]) {
        if (!Array.isArray(codes)) {
            codes = [codes]
        }

        if (codes.length === 0) {
            return []
        }

        const partSet = new Set<EshopProductPart>(parts)
        const detailPromise = partSet.has('detail') || partSet.has('brand')
            ? sendList<ProductDetailEshopApi>(ESHOP_DETAIL_PATH, { codes })
            : undefined
        const pricePromise = partSet.has('price')
            ? sendList<ProductPriceEshopApi>(ESHOP_PRICE_PATH, { codes })
            : undefined
        const unitPromise = partSet.has('unit')
            ? sendList<ProductUnitEshopApi>(ESHOP_UNIT_PATH, { codes })
            : undefined
        const availabilityPromise = partSet.has('availability')
            ? sendList<ProductAvailabilityEshopApi>(ESHOP_AVAILABILITY_PATH, { codes })
            : undefined
        const seasonPromise = partSet.has('season')
            ? sendList<ProductSeasonEshopApi>(ESHOP_SEASON_PATH, { codes })
            : undefined
        const stickerPromise = partSet.has('sticker')
            ? sendList<ProductStickerEshopListItemApi>(ESHOP_STICKER_PATH, { codes })
            : undefined

        const [detail, price, unit, availability, season, sticker] = await Promise.all([
            detailPromise,
            pricePromise,
            unitPromise,
            availabilityPromise,
            seasonPromise,
            stickerPromise,
        ])
        const responses: EshopProductApiResponses = {
            ...(detailPromise === undefined ? {} : { detail }),
            ...(pricePromise === undefined ? {} : { price }),
            ...(unitPromise === undefined ? {} : { unit }),
            ...(availabilityPromise === undefined ? {} : { availability }),
            ...(seasonPromise === undefined ? {} : { season }),
            ...(stickerPromise === undefined ? {} : { sticker }),
        }

        // @eshop/type marks requested parts as non-nullable, unlike the real API.
        return mapEshopProducts(codes, parts, responses) as (Eshop.Product<T> | null)[]
    },

    async getProductRentalByCode<T extends RentalProductPart>(this: void, codes: string | string[], parts: Array<T>) {
        if (!Array.isArray(codes)) {
            codes = [codes]
        }

        if (codes.length === 0) {
            return []
        }

        const partSet = new Set<RentalProductPart>(parts)
        const detailPromise = sendList<ProductDetailRentalApi>(RENTAL_DETAIL_PATH, { unionCode: codes })
        const stickerPromise = partSet.has('sticker')
            ? sendList<ProductStickerRentalListItemApi>(RENTAL_STICKER_PATH, { unionCode: codes })
            : undefined
        const [detail, sticker] = await Promise.all([detailPromise, stickerPromise])
        const responses: RentalProductApiResponses = {
            detail,
            ...(stickerPromise === undefined ? {} : { sticker }),
        }

        // @eshop/type marks requested parts and rental identifiers as non-nullable.
        return mapRentalProducts(codes, parts, responses) as (Rental.Product<T> | null)[]
    },
}
